import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { FiX, FiChevronDown } from 'react-icons/fi';

interface DropdownProps {
  isLabel?: boolean;
  label?: string;
  labelClassname?: string;
  withInput?: boolean;
  icon?: React.ReactNode;
  text?: string;
  options: Array<{ id?: string; [key: string]: React.ReactNode | object } | React.ReactNode>;
  optionLabel?: string;
  onSelect: (
    option: { id?: string; [key: string]: React.ReactNode } | React.ReactNode | string[]
  ) => void;
  selectedOption?:
    | { id?: string; [key: string]: React.ReactNode | object }
    | string
    | React.ReactNode;
  placeholder?: string;
  size?: string;
  heightOption?: string;
  additionalClassname?: string;
  disabled?: boolean;
  id?: string;
  required?: boolean;
  showMessageError?: boolean;
  error?: string;
  dropdownWithTitle?: string;
  showSearchInput?: boolean;
  renderAlertBox?: ReactNode;
  showClearIcon?: boolean;
  isHighZIndex?: boolean;
}

const Dropdown = ({
  isLabel = true,
  label,
  labelClassname,
  icon,
  text,
  options,
  optionLabel,
  onSelect,
  placeholder,
  disabled,
  withInput = false,
  size = 'w-full',
  additionalClassname = '',
  heightOption = 'max-h-[200px]',
  id = '',
  selectedOption = '',
  required = false,
  showMessageError = true,
  error = '',
  dropdownWithTitle,
  showSearchInput,
  renderAlertBox,
  showClearIcon = true,
  isHighZIndex = false,
}: DropdownProps) => {
  const dropdownRef = useRef(null);
  const inputDropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        e.target !== inputDropdownRef.current
      ) {
        setIsDropdownOpen(false);
      }
    };

    // didmount dropdown
    document.addEventListener('mousedown', checkIfClickedOutside);

    // unmount dropdown
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionSelect = (option) => {
    onSelect(option);
    setIsDropdownOpen(false);
  };

  const deleteSelectedOption = () => {
    if (!disabled) {
      onSelect('');
    }
  };

  const [searchInputValue, setSearchInputValue] = useState('');

  function renderAlertOrSearch() {
    if (renderAlertBox) return renderAlertBox;
    if (showSearchInput) {
      return (
        <div className="p-2 sticky top-0 bg-white">
          <input
            type="text"
            className={`w-full px-4 py-2 border border-gray-200 rounded-md outline-none`}
            placeholder={placeholder}
            value={searchInputValue}
            onChange={(e) => {
              setSearchInputValue(e.target.value);
            }}
            autoFocus
          />
        </div>
      );
    }
    return null;
  }

  return (
    <div className={`relative ${size}`}>
      {withInput ? (
        <Fragment>
          {isLabel && (
            <label className={`${labelClassname}`}>
              {label}
              {required && <span className="text-red-500"> *</span>}
            </label>
          )}

          <div
            onClick={toggleDropdown}
            className={`flex items-center ${!disabled ? 'cursor-pointer' : ''}`}
          >
            <input
              id={id}
              ref={inputDropdownRef}
              type="text"
              value={selectedOption && (selectedOption[optionLabel] || selectedOption)}
              className={`w-full outline-none px-4 py-2 ${
                disabled
                  ? 'bg-reliableBlack10 placeholder:text-reliableBlack50 text-reliableBlack50'
                  : 'bg-reliableBlack3 placeholder:text-reliableBlack60 text-reliableBlack'
              } border-b border-reliableBlack30 focus:border-reliableBlack60 lg:px-5 lg:py-4 ${additionalClassname} ${
                !disabled ? 'cursor-pointer' : ''
              }`}
              placeholder={placeholder}
              readOnly
              disabled={disabled}
            />
            {/* Handle toggle icon */}
            {!disabled ? (
              selectedOption && !dropdownWithTitle && showClearIcon ? (
                <span className="absolute right-2 text-red-500" onClick={deleteSelectedOption}>
                  {!disabled ? (
                    showClearIcon ? (
                      <FiX />
                    ) : (
                      <FiChevronDown
                        className={
                          isDropdownOpen
                            ? 'transform rotate-180 transition duration-100 text-reliableBlack80'
                            : 'text-reliableBlack80 transform rotate-0 duration-100'
                        }
                      />
                    )
                  ) : null}
                </span>
              ) : (
                <span
                  className={`absolute right-2 pointer-events-none ${
                    isDropdownOpen && !dropdownWithTitle
                      ? 'transform rotate-180 transition duration-100 text-reliableBlack80'
                      : 'text-reliableBlack80 transform rotate-0 duration-100'
                  }`}
                >
                  <FiChevronDown />
                </span>
              )
            ) : null}
          </div>
        </Fragment>
      ) : (
        <div
          onClick={toggleDropdown}
          className={`flex gap-1 items-center h-10 justify-center px-2 ${
            !disabled ? 'cursor-pointer' : ''
          }`}
        >
          {icon ? icon : <div>{text}</div>}

          {!disabled ? (
            <span>
              <MdArrowDropDown size={25} />
            </span>
          ) : null}
        </div>
      )}
      {isDropdownOpen && (
        <ul
          className={`${size} absolute ${heightOption} overflow-y-auto ${isHighZIndex ? 'z-[100]' : 'z-[49]'} left-0 text-reliableBlack80 bg-white shadow rounded ${dropdownWithTitle ? 'p-4 flex flex-col gap-2' : ''}`}
          ref={dropdownRef}
          style={
            dropdownWithTitle
              ? {
                  top:
                    (options?.length + 1) * 58 > 235
                      ? '-235px'
                      : `-${(options?.length + 1) * 58}px`,
                }
              : {}
          }
        >
          {dropdownWithTitle && (
            <li id="title" className="px-4 font-bold text-xl">
              {dropdownWithTitle}
            </li>
          )}
          {renderAlertOrSearch()}
          {options?.length ? (
            options
              ?.filter((opt) =>
                searchInputValue?.length >= 3
                  ? String(opt[optionLabel] || opt)
                      ?.toLowerCase()
                      ?.includes(searchInputValue?.toLowerCase())
                  : true
              )
              .map((option, index) => (
                <li
                  id={`dd-form-${String(option[optionLabel] || option)
                    .toLowerCase()
                    .replace(/\s/g, '-')}
              `}
                  key={option['id'] || index}
                  className={
                    dropdownWithTitle
                      ? 'p-4 border-b border-reliableBlack10 text-base hover:bg-gray-100 cursor-pointer'
                      : 'px-4 py-2 hover:bg-gray-100 cursor-pointer'
                  }
                  onClick={() => handleOptionSelect(option)}
                >
                  {option[optionLabel] || option}
                </li>
              ))
          ) : (
            <li className="px-4 py-2">Tidak ada pilihan</li>
          )}
        </ul>
      )}
      {/* --- showed when input already touched before and error has a string --- */}
      {!showMessageError ? (
        ''
      ) : error ? (
        <div className="flex items-center gap-[5.67px]">
          <span className="text-supportiveRed text-[14px] leading-[20px] font-[400]">{error}</span>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Dropdown;
